"use client";

import { useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Search,
  Users,
  Home,
  CreditCard,
  Calendar,
  ShieldCheck,
  MessageSquare,
  HelpCircle,
} from "lucide-react";

export default function FAQPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("guests");

  // Filter accordion items based on search query
  const filterItems = (items: FAQItem[]) => {
    if (!searchQuery.trim()) return items;

    return items.filter(
      (item) =>
        item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <section className="bg-primary/10 py-12">
        <div className="container mx-auto px-4">
          <Link href="/">
            <Button variant="ghost" className="mb-8 gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-4xl font-bold mb-4">Help Center</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Find answers to commonly asked questions about booking and hosting
            with Kawkb.
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search for answers..."
                className="pl-10 pr-4 py-6 text-lg rounded-lg"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8"
                  onClick={() => setSearchQuery("")}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <Tabs
            defaultValue="guests"
            value={activeTab}
            onValueChange={setActiveTab}
            className="max-w-5xl mx-auto"
          >
            <div className="mb-8">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="guests" className="gap-2">
                  <Users className="h-4 w-4" />
                  For Guests
                </TabsTrigger>
                <TabsTrigger value="hosts" className="gap-2">
                  <Home className="h-4 w-4" />
                  For Hosts
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Guest FAQs */}
            <TabsContent value="guests" className="mt-6">
              <div className="grid md:grid-cols-4 gap-8">
                {/* Categories */}
                <div className="md:col-span-1">
                  <h3 className="font-semibold text-lg mb-4">Topics</h3>
                  <div className="space-y-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setSearchQuery("booking");
                      }}
                    >
                      <Calendar className="h-4 w-4" />
                      Booking & Reservations
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setSearchQuery("payment");
                      }}
                    >
                      <CreditCard className="h-4 w-4" />
                      Payments & Refunds
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setSearchQuery("account");
                      }}
                    >
                      <Users className="h-4 w-4" />
                      Account Management
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setSearchQuery("safety");
                      }}
                    >
                      <ShieldCheck className="h-4 w-4" />
                      Safety & Security
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setSearchQuery("communication");
                      }}
                    >
                      <MessageSquare className="h-4 w-4" />
                      Communication
                    </Button>
                  </div>
                </div>

                {/* FAQ Accordions */}
                <div className="md:col-span-3">
                  <h2 className="text-2xl font-bold mb-6">
                    {searchQuery
                      ? `Search Results for "${searchQuery}"`
                      : "Frequently Asked Questions for Guests"}
                  </h2>

                  {filterItems(guestFAQs).length > 0 ? (
                    <Accordion type="single" collapsible className="w-full">
                      {filterItems(guestFAQs).map((faq, index) => (
                        <AccordionItem key={index} value={`item-${index}`}>
                          <AccordionTrigger className="text-left">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent>
                            <div
                              dangerouslySetInnerHTML={{ __html: faq.answer }}
                            />
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <div className="text-center py-12">
                      <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">
                        No Results Found
                      </h3>
                      <p className="text-gray-600 mb-6">
                        We couldn&apos;t find any FAQs matching your search.
                        Please try a different search term or browse the
                        categories.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setSearchQuery("")}
                      >
                        Clear Search
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Host FAQs */}
            <TabsContent value="hosts" className="mt-6">
              <div className="grid md:grid-cols-4 gap-8">
                {/* Categories */}
                <div className="md:col-span-1">
                  <h3 className="font-semibold text-lg mb-4">Topics</h3>
                  <div className="space-y-2">
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setSearchQuery("listing");
                      }}
                    >
                      <Home className="h-4 w-4" />
                      Creating Listings
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setSearchQuery("pricing");
                      }}
                    >
                      <CreditCard className="h-4 w-4" />
                      Pricing & Payments
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setSearchQuery("calendar");
                      }}
                    >
                      <Calendar className="h-4 w-4" />
                      Calendar Management
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setSearchQuery("requirements");
                      }}
                    >
                      <ShieldCheck className="h-4 w-4" />
                      Host Requirements
                    </Button>
                    <Button
                      variant="ghost"
                      className="w-full justify-start gap-2"
                      onClick={() => {
                        setSearchQuery("guest management");
                      }}
                    >
                      <Users className="h-4 w-4" />
                      Guest Management
                    </Button>
                  </div>
                </div>

                {/* FAQ Accordions */}
                <div className="md:col-span-3">
                  <h2 className="text-2xl font-bold mb-6">
                    {searchQuery
                      ? `Search Results for "${searchQuery}"`
                      : "Frequently Asked Questions for Hosts"}
                  </h2>

                  {filterItems(hostFAQs).length > 0 ? (
                    <Accordion type="single" collapsible className="w-full">
                      {filterItems(hostFAQs).map((faq, index) => (
                        <AccordionItem key={index} value={`item-${index}`}>
                          <AccordionTrigger className="text-left">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent>
                            <div
                              dangerouslySetInnerHTML={{ __html: faq.answer }}
                            />
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <div className="text-center py-12">
                      <HelpCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">
                        No Results Found
                      </h3>
                      <p className="text-gray-600 mb-6">
                        We couldn&apos;t find any FAQs matching your search.
                        Please try a different search term or browse the
                        categories.
                      </p>
                      <Button
                        variant="outline"
                        onClick={() => setSearchQuery("")}
                      >
                        Clear Search
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Still Need Help */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-4">Still Need Help?</h2>
          <p className="text-gray-600 max-w-xl mx-auto mb-8">
            Our support team is here to assist you with any questions or issues
            you may have.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/contact">
              <Button size="lg" className="gap-2">
                <MessageSquare className="h-4 w-4" />
                Contact Support
              </Button>
            </Link>
            <Button variant="outline" size="lg" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Live Chat
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

// Types
interface FAQItem {
  question: string;
  answer: string;
}

// Guest FAQs
const guestFAQs: FAQItem[] = [
  {
    question: "How do I search for properties on Kawkb?",
    answer:
      "You can search for properties on Kawkb by entering your destination, travel dates, and number of guests on our homepage. You can further refine your search using filters for property type, amenities, price range, and more. Our search results will show you available properties matching your criteria along with photos, pricing, and key details.",
  },
  {
    question: "What payment methods does Kawkb accept?",
    answer:
      "Kawkb accepts various payment methods including major credit cards (Visa, Mastercard, American Express), debit cards, and PayPal. In some regions, we also support Apple Pay and Google Pay. All payments are securely processed and encrypted to ensure your financial information remains safe.",
  },
  {
    question: "How do I create an account on Kawkb?",
    answer:
      "Creating an account on Kawkb is simple! Click the 'Sign Up' button in the top right corner of our website or app. You can sign up using your email address, or connect with your Google, Facebook, or Apple account for faster registration. After verifying your email, you'll need to provide some basic information to complete your profile.",
  },
  {
    question: "What is Kawkb's cancellation policy?",
    answer:
      "Kawkb offers different cancellation policies depending on the property listing. These policies are clearly displayed on each property page before you book. Generally, we offer:<br><br><ul><li><strong>Flexible:</strong> Full refund up to 7 days before check-in</li><li><strong>Moderate:</strong> Full refund up to 14 days before check-in</li><li><strong>Strict:</strong> 50% refund up to 30 days before check-in</li></ul><br>Please review the specific cancellation policy for your booking as hosts may customize these terms.",
  },
  {
    question: "How do I contact my host before or during my stay?",
    answer:
      "Once your booking is confirmed, you can message your host directly through the Kawkb platform. Simply go to your 'Trips' section, select your booking, and click on 'Message Host.' We encourage guests to communicate through our platform to ensure safety and keep a record of all communications. For urgent matters during your stay, hosts typically provide additional contact information in the check-in instructions.",
  },
  {
    question: "Is my booking confirmed immediately?",
    answer:
      "Some properties offer instant booking, which means your reservation is confirmed immediately upon payment. Other properties require host approval, in which case your booking will be pending until the host accepts it (usually within 24 hours). The booking type is clearly indicated on the property listing page before you book.",
  },
  {
    question: "What if something is not as described when I arrive?",
    answer:
      "If you arrive at a property and find significant discrepancies from the listing, please take the following steps:<br><br>1. Take photos documenting the issues<br>2. Contact your host immediately through the Kawkb messaging system to give them an opportunity to resolve the problem<br>3. If the issue isn't resolved satisfactorily within 24 hours, contact Kawkb customer support<br><br>Our customer support team will work with both you and the host to find a suitable resolution, which may include moving you to alternative accommodation or providing a partial/full refund depending on the circumstances.",
  },
  {
    question: "How does Kawkb ensure safety for guests?",
    answer:
      "At Kawkb, guest safety is our priority. We implement several measures to ensure a safe experience:<br><br><ul><li>Verified host profiles with identity verification</li><li>Property reviews from previous guests</li><li>Secure messaging system for all communications</li><li>Protected payment system</li><li>24/7 customer support for safety concerns</li><li>Property safety requirements including smoke and carbon monoxide detectors</li><li>Background checks on hosts in certain regions</li></ul><br>We also provide safety guidelines and local emergency information for each destination.",
  },
  {
    question: "Can I book on behalf of someone else?",
    answer:
      "Yes, you can book a property on behalf of someone else, but there are a few important things to note:<br><br>1. The account holder who makes the booking is responsible for the reservation<br>2. You must disclose to the host that you're booking for someone else<br>3. The primary guest staying at the property must be over 18 years old<br>4. You should add the guest's name in the 'additional information' section when booking<br><br>For security reasons, hosts may request additional verification of the actual guests upon arrival.",
  },
  {
    question: "What should I do if I need to check in late?",
    answer:
      "If you anticipate arriving later than the stated check-in time, it's important to communicate this with your host as soon as possible. Many hosts have flexible arrangements for late check-ins, but they need advance notice to make necessary arrangements. You can message your host directly through the Kawkb platform to coordinate late arrival details. Some properties offer self-check-in options with lockboxes or digital entry codes for added convenience.",
  },
  {
    question: "Are pets allowed in Kawkb properties?",
    answer:
      "Pet policies vary by property and are set by individual hosts. You can use the 'Pets allowed' filter when searching to find pet-friendly accommodations. Always check the property rules section on the listing page for specific pet policies before booking. Some hosts may allow pets with an additional fee or have restrictions on size or number of pets. If the listing doesn't explicitly state that pets are allowed, you should message the host to inquire before booking.",
  },
  {
    question: "How do I leave a review after my stay?",
    answer:
      "After check-out, you'll receive an email prompting you to leave a review. You can also access the review option by going to your 'Trips' section and selecting the completed booking. You have 14 days after check-out to leave a review. We encourage detailed, honest feedback about your experience to help future guests and hosts. Reviews on Kawkb are double-blind, meaning neither party can see the other's review until both have submitted or the 14-day period has ended.",
  },
];

// Host FAQs
const hostFAQs: FAQItem[] = [
  {
    question: "How do I list my property on Kawkb?",
    answer:
      "Listing your property on Kawkb is a straightforward process:<br><br>1. Create a host account or log in to your existing account<br>2. Click 'Become a Host' or 'Add Listing' in your dashboard<br>3. Provide details about your property (location, type, size, amenities)<br>4. Upload high-quality photos of your space<br>5. Set your availability, house rules, and pricing<br>6. Complete any verification requirements<br>7. Submit your listing for review<br><br>Our team will review your listing, and once approved, it will be published on our platform. We offer a step-by-step guide throughout the process to help you create an attractive listing.",
  },
  {
    question: "What are Kawkb's host fees?",
    answer:
      "Kawkb typically charges hosts a service fee of 3-5% of the booking subtotal (before fees and taxes). This fee varies based on your cancellation policy—stricter cancellation policies have lower host fees. The exact fee percentage will be displayed during the listing setup process and on your earnings dashboard. This fee covers the cost of platform operations, payment processing, customer support, and marketing to bring guests to your property.",
  },
  {
    question: "How do I set pricing for my property?",
    answer:
      "Setting the right price for your property is crucial for success on Kawkb. You can:<br><br>1. Use our Smart Pricing tool that suggests optimal rates based on local demand, seasonality, and similar properties<br>2. Set custom base prices for weekdays, weekends, and seasonal periods<br>3. Add special pricing for holidays or local events<br>4. Offer discounts for weekly or monthly stays<br>5. Use dynamic pricing that automatically adjusts based on demand<br><br>Remember to factor in all your costs, including cleaning fees, utilities, and our service fee when determining your pricing strategy. You can adjust your pricing at any time through your host dashboard.",
  },
  {
    question: "How does the calendar management work?",
    answer:
      "Kawkb's calendar management system allows you to:<br><br>1. Set your property's availability with a user-friendly calendar interface<br>2. Block dates when your property is unavailable<br>3. Set minimum and maximum stay requirements<br>4. Create custom seasonal rules (like requiring Saturday check-ins during summer)<br>5. Sync with other platforms if you list elsewhere using iCal integration<br><br>It's important to keep your calendar up-to-date to avoid double bookings and cancelations. You can manage your calendar from both desktop and mobile devices through your host dashboard.",
  },
  {
    question: "What are the requirements to become a host on Kawkb?",
    answer:
      "To become a host on Kawkb, you'll need to meet these requirements:<br><br>1. Be at least 18 years old<br>2. Complete identity verification (usually requiring government ID)<br>3. Provide accurate information about your property and amenities<br>4. Ensure your property meets our minimum quality and safety standards<br>5. Have legal authority to rent the space (ownership, landlord permission, etc.)<br>6. Comply with local regulations regarding short-term rentals<br>7. Maintain good communication and response times<br><br>For certain premium property tiers, additional quality standards may apply.",
  },
  {
    question: "How do I handle guest check-ins and check-outs?",
    answer:
      "You have several options for managing guest check-ins and check-outs:<br><br>1. <strong>In-person greeting:</strong> Meet guests personally to hand over keys and show them around<br>2. <strong>Self check-in:</strong> Provide lockbox codes, smart locks, or keypads for contactless entry<br>3. <strong>Property manager:</strong> Hire someone to handle check-ins on your behalf<br><br>Regardless of method, be sure to provide clear check-in instructions at least 48 hours before arrival. For check-out, leave simple instructions about key return, trash disposal, and any other closing procedures in your house manual. Many hosts find that providing a digital guide with photos makes the process smoother for guests.",
  },
  {
    question: "When and how do I get paid for bookings?",
    answer:
      "Kawkb typically processes payments to hosts 24 hours after guest check-in. This system ensures both parties are satisfied with the arrangement before releasing funds. You can receive payments through:<br><br>1. Direct bank deposit (preferred method for most hosts)<br>2. PayPal<br>3. Other local payment methods depending on your country<br><br>To set up or change your payment method, go to the 'Account' section in your host dashboard and select 'Payout Preferences.' Payments are processed in the currency you've selected for your account.",
  },
  {
    question: "What happens if a guest damages my property?",
    answer:
      "If a guest damages your property, follow these steps:<br><br>1. Document the damage with photos immediately upon discovery<br>2. Contact the guest through the Kawkb messaging system to inform them<br>3. Submit a resolution request through our Resolution Center within 14 days of checkout<br>4. Provide evidence of the damage and estimated repair costs<br><br>Kawkb offers a Property Protection program that covers eligible damages up to $1,000,000 USD. Your claim will be reviewed by our team, and if approved, you'll receive compensation according to our terms and conditions. For minor damages, we often encourage direct resolution between hosts and guests first.",
  },
  {
    question: "How can I improve my property's ranking in search results?",
    answer:
      "To improve your property's visibility in search results:<br><br>1. <strong>Complete your listing</strong> with detailed descriptions and high-quality photos<br>2. <strong>Maintain competitive pricing</strong> for your area and property type<br>3. <strong>Respond quickly</strong> to inquiries and booking requests (aim for 100% response rate)<br>4. <strong>Keep your calendar up-to-date</strong> to show accurate availability<br>5. <strong>Earn positive reviews</strong> by providing excellent guest experiences<br>6. <strong>Minimize cancellations</strong> as these significantly impact ranking<br>7. <strong>Offer instant booking</strong> if possible<br>8. <strong>Update your listing</strong> regularly with seasonal information<br><br>Our search algorithm prioritizes listings that provide consistent, high-quality guest experiences and active host engagement.",
  },
  {
    question:
      "What should I include in my property description to attract bookings?",
    answer:
      "A compelling property description should include:<br><br>1. <strong>Unique selling points</strong> that set your property apart<br>2. <strong>Accurate details</strong> about space, beds, baths, and amenities<br>3. <strong>Local attractions</strong> and distance to popular destinations<br>4. <strong>Special features</strong> like views, outdoor spaces, or historical significance<br>5. <strong>Practical information</strong> about transportation and accessibility<br>6. <strong>Personal touches</strong> that make your space welcoming<br>7. <strong>Clear house rules</strong> to set expectations<br>8. <strong>Neighborhood highlights</strong> including restaurants and shopping<br><br>Use descriptive language that helps guests imagine themselves in your space. Be honest about any limitations to avoid disappointment. Update your description seasonally if relevant (e.g., mentioning nearby ski slopes in winter or beach access in summer).",
  },
  {
    question: "How do taxes work for my Kawkb income?",
    answer:
      "Tax obligations for Kawkb hosts vary by country and region:<br><br>1. In many locations, rental income must be reported on your tax return<br>2. Some jurisdictions require collection and remittance of local occupancy or tourism taxes<br>3. Kawkb automatically collects and remits certain taxes in many regions, but not all<br>4. You may be eligible for deductions related to your hosting expenses<br><br>We provide annual earning summaries to help with your tax reporting, but we strongly recommend consulting with a tax professional familiar with short-term rental regulations in your area. It's important to understand your obligations before you start hosting to avoid potential penalties.",
  },
  {
    question: "What should I do if I need to cancel a booking?",
    answer:
      "While we strongly discourage host cancellations, we understand that emergencies happen. If you must cancel a confirmed booking:<br><br>1. Go to your 'Reservations' section in your dashboard<br>2. Select the booking you need to cancel<br>3. Click 'Cancel reservation' and provide a reason<br>4. Consider offering the guest alternative dates or assistance finding another property<br><br>Please note that host cancellations have significant consequences:<br>- An automatic review noting the cancellation will appear on your listing<br>- Your calendar will be blocked for those dates<br>- A cancellation fee may be charged<br>- Multiple cancellations may result in account penalties or suspension<br><br>Always try to honor your bookings or provide as much advance notice as possible if cancellation is unavoidable.",
  },
];
